import { useEffect, useState, type FormEvent } from "react";
import { boardApi, epicsApi, teamsApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import type { Team } from "../api/types";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import { useConfirm } from "../components/ConfirmDialog";
import { formatDate } from "../utils/time";

interface Counts {
  tickets: number; // -1 = unknown
  epics: number;
}

export function TeamsPage() {
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [counts, setCounts] = useState<Record<string, Counts>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const confirm = useConfirm();

  const loadCounts = async (list: Team[]) => {
    const entries = await Promise.all(
      list.map(async (t) => {
        const [board, eps] = await Promise.all([
          boardApi.get(t.id).catch(() => null),
          epicsApi.listByTeam(t.id).catch(() => []),
        ]);
        const tickets = board ? board.columns.reduce((n, c) => n + c.tickets.length, 0) : -1;
        return [t.id, { tickets, epics: eps.length }] as const;
      }),
    );
    setCounts(Object.fromEntries(entries));
  };

  const load = async () => {
    setError(null);
    try {
      const list = await teamsApi.list();
      setTeams(list);
      setCounts({});
      void loadCounts(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load teams.");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await teamsApi.create(newName.trim());
      setNewName("");
      setCreating(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create team.");
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing?.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await teamsApi.rename(editing.id, editing.name.trim());
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to rename team.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (team: Team) => {
    const ok = await confirm({
      title: "Delete team",
      message: `Delete team “${team.name}”? This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setError(null);
    try {
      await teamsApi.remove(team.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete team.");
    }
  };

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Teams</h1>
          <p className="page-subtitle">All verified users can view and manage all teams.</p>
        </div>
        <button className="btn-primary" onClick={() => setCreating(true)}>
          <Icon name="plus" size={18} /> Create team
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      {teams === null ? (
        <Loading />
      ) : teams.length === 0 ? (
        <EmptyState message="No teams yet. Create your first team to get started." />
      ) : (
        <div className="card table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="num">Tickets</th>
                <th className="num">Epics</th>
                <th>Modified</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => {
                const c = counts[team.id];
                const known = !!c;
                const referenced = known && (c.tickets > 0 || c.epics > 0);
                const canDelete = known && !referenced;
                return (
                  <tr key={team.id}>
                    <td className="cell-main" data-label="Name">
                      <div className="cell-title">{team.name}</div>
                    </td>
                    <td className="num" data-label="Tickets">
                      {!known ? "…" : c.tickets < 0 ? "—" : c.tickets}
                    </td>
                    <td className="num" data-label="Epics">
                      {!known ? "…" : c.epics}
                    </td>
                    <td data-label="Modified">{formatDate(team.modifiedAt)}</td>
                    <td data-label="Actions">
                      <div className="table-actions">
                        <button
                          className="secondary btn-sm"
                          onClick={() => setEditing({ id: team.id, name: team.name })}
                        >
                          <Icon name="edit" size={15} /> Edit
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => remove(team)}
                          disabled={!canDelete}
                          title={referenced ? "Has tickets or epics — cannot delete" : "Delete team"}
                        >
                          <Icon name="trash" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="table-note">Delete is disabled while a team contains tickets or epics.</p>
        </div>
      )}

      {creating && (
        <Modal title="Create team" onClose={() => setCreating(false)}>
          <form className="stack" onSubmit={create}>
            <label className="field">
              <span className="field-label">Team name</span>
              <input
                autoFocus
                placeholder="e.g. Platform Engineering"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </label>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={busy || !newName.trim()}>
                Create
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title="Rename team" onClose={() => setEditing(null)}>
          <form className="stack" onSubmit={saveRename}>
            <label className="field">
              <span className="field-label">Team name</span>
              <input
                autoFocus
                value={editing.name}
                onChange={(e) => setEditing({ id: editing.id, name: e.target.value })}
              />
            </label>
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={busy || !editing.name.trim()}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}
